import { useQueryClient } from '@tanstack/react-query'
import { usePutSettingsStateCode, getGetSettingsQueryKey } from '@/api/generated/setting/setting'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { SAVE_STATE_SETTINGS_SUCCESS } from '@/store/state-settings/StateSettingsConstants'
import { getStateSettingsErrorMessage } from './getStateSettingsErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

interface SaveStateSettingsCallbacks {
  onSetLoading: (value: boolean) => void
  onSuccess: () => void
  onError: (message: string) => void
}

/**
 * Saves (PUT) the per-state settings. Replaces the `updateStateSettings` thunk + the
 * SAVE_STATE_SETTINGS* flow and the StateSettingsListener side effects.
 *
 * Old side effects, reproduced via caller callbacks:
 * - SAVE_STATE_SETTINGS -> onSetLoading(true) before the request.
 * - SAVE_STATE_SETTINGS_SUCCESS -> onSetLoading(false) + the navigate/logout decision (the page
 *   passes the navigate/logout/sentry logic in `onSuccess`).
 * - SAVE_STATE_SETTINGS_ERROR -> onSetLoading(false) + onError(mapped message).
 *
 * Cross-domain preservation: `management.statesSettings` is also read by NewVisit / FilesSection
 * and seeded by the drugRules boot fetch, so we keep that Redux field fresh after a save by
 * dispatching the existing SAVE_STATE_SETTINGS_SUCCESS in-place reducer update (same extraData
 * shape the thunk produced), in addition to invalidating the React Query settings cache so the
 * States page + edit flow (useStateSettings) refetch.
 */
export const useSaveStateSettings = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { mutate } = usePutSettingsStateCode()

  const saveStateSettings = (
    stateCode: string,
    settings: ApplicationSettingsPerStateAdminApiContract,
    callbacks: SaveStateSettingsCallbacks,
  ) => {
    callbacks.onSetLoading(true)
    mutate(
      { stateCode, data: settings },
      {
        onSuccess: () => {
          dispatch({
            type: SAVE_STATE_SETTINGS_SUCCESS,
            extraData: { settings, stateCode },
          })
          void queryClient.invalidateQueries({ queryKey: getGetSettingsQueryKey() })
          callbacks.onSetLoading(false)
          callbacks.onSuccess()
        },
        onError: (error) => {
          callbacks.onSetLoading(false)
          callbacks.onError(getStateSettingsErrorMessage(getServiceApiErrorBody(error)))
        },
      },
    )
  }

  return { saveStateSettings }
}
